import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ArrowRight, X } from 'lucide-react';
import { Card } from '@components/ui';
import { getBlocksByConstructionId } from '@mocks/bloklar';
import { getBlokDaireleri, getDaireIlerleme, durumlar } from '@mocks/daireIlerleme';
import clsx from 'clsx';

export default function HizliDaireErisim({ insaatlar }) {
    const navigate = useNavigate();
    const [selectedInsaat, setSelectedInsaat] = useState('');
    const [selectedBlok, setSelectedBlok] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [showResults, setShowResults] = useState(false);

    // Seçili inşaata ait bloklar
    const bloklar = useMemo(() => {
        if (!selectedInsaat) return [];
        return getBlocksByConstructionId(selectedInsaat);
    }, [selectedInsaat]);

    // Seçili blokun daireleri (ilerleme verileriyle)
    const daireler = useMemo(() => {
        if (!selectedBlok) return [];
        const blokData = getBlokDaireleri(selectedBlok);
        if (!blokData) return [];
        const list = [];
        blokData.katlar.forEach(kat => {
            kat.daireler.forEach(daire => {
                const il = getDaireIlerleme(daire.id);
                list.push({
                    ...daire,
                    genelIlerleme: il?.genelIlerleme || 0,
                    durum: il?.durum || 'baslanmadi',
                });
            });
        });
        return list;
    }, [selectedBlok]);

    // Filtrelenmiş daireler
    const filteredDaireler = useMemo(() => {
        if (!searchTerm) return daireler;
        const s = searchTerm.toLowerCase();
        return daireler.filter(d => d.no.toLowerCase().includes(s) || d.tip.toLowerCase().includes(s));
    }, [daireler, searchTerm]);

    const handleInsaatChange = (val) => {
        setSelectedInsaat(val);
        setSelectedBlok('');
        setSearchTerm('');
        setShowResults(false);
    };

    const handleBlokChange = (val) => {
        setSelectedBlok(val);
        setSearchTerm('');
        setShowResults(true);
    };

    const getProgressColor = (y) => y >= 75 ? 'bg-success' : y >= 40 ? 'bg-primary' : y > 0 ? 'bg-warning' : 'bg-bg-tertiary';
    const getProgressTextColor = (y) => y >= 75 ? 'text-success' : y >= 40 ? 'text-primary' : y > 0 ? 'text-warning' : 'text-text-muted';

    const selectClass = "w-full px-3 py-2.5 rounded-lg border border-border-light bg-bg-primary text-text-primary text-sm focus:outline-none focus:border-primary";

    return (
        <Card padding="none" className="overflow-hidden">
            <div className="p-4 md:p-5 border-b border-border-light bg-gradient-to-r from-primary/5 to-secondary/5">
                <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                        <Search size={20} className="text-primary" />
                    </div>
                    <div>
                        <h2 className="font-semibold text-text-primary text-base">Daire Bul & Güncelle</h2>
                        <p className="text-xs text-text-muted">İnşaat → Blok → Daire seçerek hızlıca ilerleme sayfasına gidin</p>
                    </div>
                </div>

                {/* Cascade Selectors */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {/* İnşaat seçimi */}
                    <div>
                        <label className="block text-xs font-medium text-text-secondary mb-1">📐 İnşaat</label>
                        <select
                            value={selectedInsaat}
                            onChange={e => handleInsaatChange(e.target.value)}
                            className={selectClass}
                        >
                            <option value="">İnşaat seçin...</option>
                            {insaatlar.map(ins => (
                                <option key={ins.id} value={ins.id}>{ins.name}</option>
                            ))}
                        </select>
                    </div>

                    {/* Blok seçimi */}
                    <div>
                        <label className="block text-xs font-medium text-text-secondary mb-1">🏗️ Blok</label>
                        <select
                            value={selectedBlok}
                            onChange={e => handleBlokChange(e.target.value)}
                            className={selectClass}
                            disabled={!selectedInsaat}
                        >
                            <option value="">Blok seçin...</option>
                            {bloklar.map(blok => (
                                <option key={blok.id} value={blok.id}>{blok.name}</option>
                            ))}
                        </select>
                    </div>

                    {/* Daire arama */}
                    <div>
                        <label className="block text-xs font-medium text-text-secondary mb-1">🔍 Daire Ara</label>
                        <div className="relative">
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                placeholder={selectedBlok ? 'Daire no yazın...' : 'Önce blok seçin'}
                                disabled={!selectedBlok}
                                className={selectClass + ' pr-8'}
                            />
                            {searchTerm && (
                                <button onClick={() => setSearchTerm('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary">
                                    <X size={14} />
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Sonuçlar */}
            {showResults && selectedBlok && (
                <div className="p-3 md:p-4 max-h-[260px] overflow-y-auto">
                    {filteredDaireler.length === 0 ? (
                        <p className="text-center text-sm text-text-muted py-6">Daire bulunamadı</p>
                    ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                            {filteredDaireler.map(daire => {
                                const d = durumlar[daire.durum];
                                return (
                                    <button
                                        key={daire.id}
                                        onClick={() => navigate(`/insaat/projeler/${selectedInsaat}/blok/${selectedBlok}/daire/${daire.id}`)}
                                        className={clsx(
                                            'flex flex-col items-start p-3 rounded-xl border transition-all text-left',
                                            'hover:shadow-md hover:border-primary/30 active:scale-[0.97]',
                                            'border-border-light bg-bg-primary'
                                        )}
                                    >
                                        <div className="flex items-center justify-between w-full mb-1.5">
                                            <span className="font-semibold text-sm text-text-primary">{daire.no}</span>
                                            <ArrowRight size={14} className="text-text-muted" />
                                        </div>
                                        <span className="text-2xs text-text-muted mb-2">{daire.tip} • {daire.kat}. Kat</span>
                                        <div className="w-full">
                                            <div className="h-1.5 bg-bg-tertiary rounded-full overflow-hidden mb-1">
                                                <div
                                                    className={clsx('h-full rounded-full transition-all', getProgressColor(daire.genelIlerleme))}
                                                    style={{ width: `${daire.genelIlerleme}%` }}
                                                />
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span className={clsx('text-2xs font-bold', getProgressTextColor(daire.genelIlerleme))}>%{daire.genelIlerleme}</span>
                                                <span className={clsx('text-2xs px-1.5 py-0.5 rounded-full', d.renk)}>{d.label}</span>
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
        </Card>
    );
}
